import React, { useState } from "react";
import { Share2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { tryNativeShare, formatConditionLine, shortAgo } from "@/lib/share";
import SharePopupMenu from "@/components/SharePopupMenu";

/**
 * Share the latest condition of ONE trail. Tries the native share sheet
 * (WhatsApp/IG/FB/Telegram on mobile) and falls back to a dropdown popup with
 * direct social links + copy on desktop.
 */
export default function ShareTrailStatusButton({ trailName, trailSlug, latestReport, className = "" }) {
    const { t, lang } = useLang();
    const [open, setOpen] = useState(false);

    if (!latestReport) return null;

    const conditionLabel = t(`reports.conditions.${latestReport.condition}`);
    const when = shortAgo(latestReport.created_at, lang);
    const line = formatConditionLine({
        name: trailName,
        conditionLabel,
        rating: latestReport.rating,
        userName: latestReport.user_name,
        when,
        dot: "›",
    });
    const note = latestReport.comment ? `\n"${latestReport.comment}"` : "";
    const url = `${window.location.origin}/trail/${trailSlug}`;
    const text = `🚵 ${t("reports.shareTitleSingle").replace("{name}", trailName.toUpperCase())}\n${line}${note}`;

    const onClick = async () => {
        const result = await tryNativeShare({ title: trailName, text, url });
        if (!result.used) setOpen((o) => !o);
    };

    return (
        <div className="relative inline-block">
            <button
                type="button"
                onClick={onClick}
                data-testid={`share-trail-status-${trailSlug}`}
                className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold border border-abnormal-acid text-abnormal-acid hover:bg-abnormal-acid hover:text-black transition-colors px-3 py-2 ${className}`}
            >
                <Share2 size={13} />
                {t("reports.share")}
            </button>
            {open && (
                <SharePopupMenu
                    text={text}
                    url={url}
                    title={t("reports.shareTitleSingle").replace("{name}", trailName.toUpperCase())}
                    onClose={() => setOpen(false)}
                    testidPrefix={`share-trail-status-${trailSlug}`}
                />
            )}
        </div>
    );
}
